'use strict';

const cacheConfig = require('public/js/lib/cache-config');
const { getItem, setItem } = require('public/js/lib/local-storage');
const STORAGE_KEYS = require('public/js/lib/storage-keys');
const { SUPPLIER_CHANGED_EVENT, SUPPLIER_RESOLVED_EVENT } = require('public/js/lib/supplier-events');
const { dispatchSteerSupplierUpdate } = require('public/js/lib/steer-outbound');

/**
 * A supplier is only actionable with both halves: the hash identifies it for diffing and checkout,
 * the name is what every surface displays.
 *
 * @param {unknown} supplier
 * @returns {boolean}
 */
function isUsableSupplier(supplier) {
	return Boolean(supplier)
		&& typeof supplier === 'object'
		&& typeof supplier.hash === 'string' && supplier.hash !== ''
		&& typeof supplier.name === 'string' && supplier.name !== '';
}

/**
 * The supplier this page is currently displaying, or null when none is cached.
 *
 * @returns {{ hash: string, name: string, interactiveGarageDealerCode?: string }|null}
 */
function getSelectedSupplier() {
	const cached = getItem(STORAGE_KEYS.SUPPLIER_V1);

	return isUsableSupplier(cached) ? cached : null;
}

/**
 * Sole owner of the supplier cache and of both supplier broadcasts.
 *
 * Callers hand over whatever supplier the server reported; this decides whether that differs from
 * what the page is displaying. Because the decision is a hash diff against the cache rather than a
 * server-reported transition, repeated and concurrent calls converge instead of racing: the second
 * call sees a matching hash and stays silent.
 *
 * A missing or incomplete supplier is ignored rather than treated as "cleared". Only explicit
 * actions may unselect a supplier, so a stale or failed session read cannot blank out the UI.
 *
 * @param {{ hash?: string, name?: string, interactiveGarageDealerCode?: string }|null|undefined} supplier
 * @returns {boolean} True when the supplier changed and the event was emitted.
 */
function reconcileSelectedSupplier(supplier) {
	if (!isUsableSupplier(supplier)) {
		return false;
	}

	const previousSupplier = getSelectedSupplier();
	const previousHash = previousSupplier === null ? null : previousSupplier.hash;
	const supplierChanged = previousHash !== supplier.hash;

	// Written before the diff so an unchanged supplier still extends the cache TTL.
	setItem(STORAGE_KEYS.SUPPLIER_V1, supplier, { ttl: cacheConfig.getTtlFor('supplier') });

	if (supplierChanged) {
		rp$(document).trigger(SUPPLIER_CHANGED_EVENT, [{ previousHash, supplier }]);
		dispatchSteerSupplierUpdate(supplier.hash);
	}

	// Always announced, even when the cache already agreed: the server's answer is the only thing
	// that can tell a surface its rendered markup is stale, and a page can render without a supplier
	// the cache already holds. Gating this on `supplierChanged` is what left such a page stuck.
	rp$(document).trigger(SUPPLIER_RESOLVED_EVENT, [{ supplier }]);

	return supplierChanged;
}

module.exports = {
	getSelectedSupplier,
	reconcileSelectedSupplier,
};
